import { Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { HttpService } from '../shared/services/http.service';
import { TreeDataService } from '../tree-data.service';
import { TreeComponent } from '../tree/tree.component';
import { TreeItem } from '../tree.model';

@Component({
  selector: 'app-mass-section',
  standalone: true,
  imports: [CommonModule, FormsModule, TreeComponent],
  templateUrl: './mass-section.component.html',
  styleUrls: ['./mass-section.component.scss']
})
export class MassSectionComponent implements OnInit {

  // Left pane
  sourceId: string | null = null;
  sourceSearch = '';
  sourceResults: any[] = [];
  showSourceDropdown = false;
  sourceMetadata: any = null;
  sourceItems: TreeItem[] = [];
  sourceLoading = false;
  selectedRouteId: string | null = null;
  private videos: Map<string, string> = new Map();
  private sourceSubject = new Subject<string>();

  // Modal
  showUpdateModal = false;
  updateLoading = false;
  updateSubmitted = false;
  updateError: string | null = null;
  updateSuccess = false;

  // Right pane — targets list
  targetKind = '';
  targetSearch = '';
  targets: any[] = [];
  targetLoading = false;
  selectedIds = new Set<string>();
  pageSize = 20;
  currentPage = 0;

  // Right pane — preview
  previewId: string | null = null;
  previewMetadata: any = null;
  previewItems: TreeItem[] = [];
  previewLoading = false;
  targetSectionSelections: Record<string, number> = {};
  autoSelectLoading = false;

  get selectedPreviewSectionId(): string | null {
    if (!this.previewId) return null;
    const index = this.targetSectionSelections[this.previewId];
    if (index === undefined) return null;
    return this.previewItems[index]?.id ?? null;
  }

  // Resize
  leftWidth = 35;
  rightWidth = 35;
  protected resizing: 'left' | 'right' | null = null;
  private resizeStartX = 0;
  private resizeStartLeft = 0;
  private resizeStartRight = 0;

  get totalPages(): number {
    return Math.ceil(this.targets.length / this.pageSize);
  }

  get pagedTargets(): any[] {
    const start = this.currentPage * this.pageSize;
    return this.targets.slice(start, start + this.pageSize);
  }

  get allSelected(): boolean {
    return this.targets.length > 0 && this.selectedIds.size === this.targets.length;
  }

  get selectedSection(): string | null {
    if (!this.selectedRouteId) return null;
    return this.sourceItems.find(i => i.id === this.selectedRouteId)?.tag ?? null;
  }

  get selectedSectionIndex(): number {
    return this.sourceItems.findIndex(i => i.id === this.selectedRouteId);
  }

  get canUpdate(): boolean {
    if (!this.sourceId || this.selectedSectionIndex === -1 || this.selectedIds.size === 0) return false;
    return Array.from(this.selectedIds).some(id => this.targetSectionSelections[id] !== undefined);
  }

  constructor(
    private httpService: HttpService,
    private treeService: TreeDataService,
    private router: Router,
    private route: ActivatedRoute,
    private el: ElementRef,
  ) {}

  startResize(handle: 'left' | 'right', event: MouseEvent) {
    this.resizing = handle;
    this.resizeStartX = event.clientX;
    this.resizeStartLeft = this.leftWidth;
    this.resizeStartRight = this.rightWidth;
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent) {
    if (!this.resizing) return;
    const shellWidth = (this.el.nativeElement as HTMLElement).offsetWidth;
    const delta = ((event.clientX - this.resizeStartX) / shellWidth) * 100;
    const min = 15;
    if (this.resizing === 'left') {
      this.leftWidth = Math.max(min, Math.min(this.resizeStartLeft + delta, 100 - min * 2 - this.resizeStartRight));
    } else {
      this.rightWidth = Math.max(min, Math.min(this.resizeStartRight - delta, 100 - min * 2 - this.resizeStartLeft));
    }
  }

  @HostListener('document:mouseup')
  onDocumentMouseUp() {
    this.resizing = null;
  }

  async ngOnInit() {
    this.sourceSubject.pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(term => this._fetchSourceResults(term));

    const videosResponse: any = await this.httpService.getAsync('hcontent/getVideos');
    if (videosResponse?.success) {
      this.videos = videosResponse.data.reduce((acc: Map<string, string>, video: any) => {
        acc.set(video.id, video.title);
        return acc;
      }, new Map<string, string>());
    }

    const { sourceId, kind, search, previewId } = await firstValueFrom(this.route.queryParams) as any;

    this._fetchSourceResults('');

    if (sourceId) {
      await this._loadSourceById(sourceId);
    }

    if (kind) {
      this.targetKind = kind;
      this.targetSearch = search || '';
      await this.searchTargets();
    }

    if (previewId) {
      const match = this.targets.find(t => String(t.hcontent_id) === String(previewId));
      if (match) await this.loadTargetPreview(match.hcontent_id);
    }
  }

  // --- Source pane ---

  onSourceSearch(term: string) {
    this.showSourceDropdown = true;
    this.sourceSubject.next(term);
  }

  async _fetchSourceResults(term: string) {
    try {
      const res: any = await this.httpService.postAsync('builder/getTemplates', { search: term, template: false });
      this.sourceResults = Array.isArray(res) ? res : (res.data || []);
    } catch (e) {
      this.sourceResults = [];
    }
  }

  async selectSource(item: any) {
    this.showSourceDropdown = false;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sourceId: item.id },
      queryParamsHandling: 'merge',
    });
    await this._loadSourceById(item.id);
  }

  async _loadSourceById(id: string) {
    this.sourceId = id;
    this.sourceItems = [];
    this.sourceMetadata = null;
    this.sourceLoading = true;
    try {
      const res: any = await this.httpService.postAsync('builder/build', { hcontentId: id });
      this.sourceMetadata = res?.config?.metadata?.properties ?? {};
      this.sourceSearch = this.sourceMetadata?.name?.replaceAll('"', '') || '';
      if (res?.config) {
        this.sourceItems = this.treeService.loadTraining(res.config, this.videos);
      }
    } catch (e) {
      console.error('Failed to load source training', e);
    } finally {
      this.sourceLoading = false;
    }
  }

  onTreeItemClicked(item: TreeItem) {
    if (item.level === 0) {
      this.selectedRouteId = this.selectedRouteId === item.id ? null : item.id;
    }
  }

  async autoSelectSection() {
    if (!this.selectedSection) return;
    this.autoSelectLoading = true;
    const overrides = { ...this.targetSectionSelections };
    const next = new Set(this.selectedIds);
    await Promise.all(this.targets.map(async (target) => {
      try {
        const res: any = await this.httpService.postAsync('builder/build', { hcontentId: target.hcontent_id });
        const sections = this.treeService.loadTraining(res.config, this.videos);
        const index = sections.findIndex(s => s.tag === this.selectedSection);
        if (index !== -1) {
          overrides[target.hcontent_id] = index;
          next.add(target.hcontent_id);
        }
      } catch { /* skip */ }
    }));
    this.targetSectionSelections = overrides;
    this.selectedIds = next;
    this.autoSelectLoading = false;
  }

  onPreviewItemClicked(item: TreeItem) {
    if (item.level !== 0 || !this.previewId) return;
    const index = this.previewItems.findIndex(i => i.id === item.id);
    if (index === -1) return;
    const current = this.targetSectionSelections[this.previewId];
    if (current === index) {
      const { [this.previewId]: _, ...rest } = this.targetSectionSelections;
      this.targetSectionSelections = rest;
    } else {
      this.targetSectionSelections = { ...this.targetSectionSelections, [this.previewId]: index };
      const next = new Set(this.selectedIds);
      next.add(this.previewId);
      this.selectedIds = next;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (this.showSourceDropdown && !target.closest('.ms-search')) {
      this.showSourceDropdown = false;
    }
  }

  // --- Target pane ---

  async searchTargets() {
    if (!this.targetKind) return;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { kind: this.targetKind, search: this.targetSearch || null },
      queryParamsHandling: 'merge',
    });
    this.targetLoading = true;
    this.targets = [];
    this.selectedIds = new Set();
    this.targetSectionSelections = {};
    try {
      const res: any = await this.httpService.postAsync('hcontent/listContent', {
        search: this.targetSearch,
        template: false,
        kind: this.targetKind,
        type: 'training'
      });
      this.targets = Array.isArray(res) ? res : (res.data || []);
      this.currentPage = 0;
    } catch (e) {
      console.error('Failed to load targets', e);
    } finally {
      this.targetLoading = false;
    }
  }

  async loadTargetPreview(id: string) {
    if (this.previewId === id && this.previewItems.length > 0) return;
    this.previewId = id;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { previewId: id },
      queryParamsHandling: 'merge',
    });
    this.previewItems = [];
    this.previewMetadata = null;
    this.previewLoading = true;
    try {
      const res: any = await this.httpService.postAsync('builder/build', { hcontentId: id });
      this.previewMetadata = res?.config?.metadata?.properties ?? {};
      if (res?.config) {
        this.previewItems = this.treeService.loadTraining(res.config, this.videos);
      }
    } catch (e) {
      console.error('Failed to load target preview', e);
    } finally {
      this.previewLoading = false;
    }
  }

  prevPage() {
    if (this.currentPage > 0) this.currentPage--;
  }

  nextPage() {
    if (this.currentPage < this.totalPages - 1) this.currentPage++;
  }

  toggleSelectAll() {
    if (this.allSelected) {
      this.selectedIds = new Set();
      this.targetSectionSelections = {};
      this.previewId = null;
      this.previewItems = [];
      this.previewMetadata = null;
    } else {
      this.selectedIds = new Set(this.targets.map(t => t.hcontent_id));
    }
  }

  toggleTarget(id: string) {
    const next = new Set(this.selectedIds);
    if (next.has(id)) {
      next.delete(id);
      const { [id]: _, ...rest } = this.targetSectionSelections;
      this.targetSectionSelections = rest;
      if (String(this.previewId) === String(id)) {
        this.previewId = null;
        this.previewItems = [];
        this.previewMetadata = null;
      }
    } else {
      next.add(id);
    }
    this.selectedIds = next;
  }

  openUpdateModal() {
    this.updateError = null;
    this.updateSuccess = false;
    this.updateSubmitted = false;
    this.showUpdateModal = true;
  }

  closeUpdateModal() {
    if (this.updateLoading) return;
    this.showUpdateModal = false;
  }

  async confirmUpdate() {
    if (!this.sourceId || !this.selectedSection || this.selectedIds.size === 0) return;
    this.updateLoading = true;
    this.updateError = null;
    try {
      const sectionOverrides: Record<string, number> = {};
      for (const id of this.selectedIds) {
        if (this.targetSectionSelections[id] !== undefined) {
          sectionOverrides[id] = this.targetSectionSelections[id];
        }
      }
      const res: any = await this.httpService.postAsync('hcontent/sectionReplace', {
        hcontentId: this.sourceId,
        ids: Array.from(this.selectedIds),
        sectionIndex: this.selectedSectionIndex,
        ...(Object.keys(sectionOverrides).length > 0 && { sectionOverrides }),
      });
      this.updateSuccess = res?.success === true;
      if (!this.updateSuccess) {
        this.updateError = res?.message || 'Something went wrong. Please try again.';
      }
    } catch (e: any) {
      this.updateSuccess = false;
      this.updateError = e?.message || 'Something went wrong. Please try again.';
    } finally {
      this.updateLoading = false;
      this.updateSubmitted = true;
    }
  }

  openInEditor(id: string) {
    this.router.navigate(['/content-editor', id]);
  }

  goBack() {
    this.router.navigate(['/content']);
  }
}
